import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Input,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import TableData from 'components/TableData';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaEdit, FaHeartBroken, FaPlus, FaTrash } from 'react-icons/fa';
import { Column } from 'react-table';
import { deleteAccount, getAccounts, toggleAccountFavorite } from 'services/api.service';
import { useDebounce } from 'react-use';
import FavoriteFormModal, { FavoriteAccount } from './FavoriteFormModal';

export default function FavoritesPage() {
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [data, setData] = useState<FavoriteAccount[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FavoriteAccount | null>(null);
  useDebounce(() => setDebouncedFilter(filter), 300, [filter]);

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await getAccounts({
        filter: debouncedFilter || undefined,
        isFavorite: true,
        page: 0,
        limit: 100,
      });
      setData(res.registers || []);
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar favorecidos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [debouncedFilter, toast]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleRemoveFavorite = async (id: string) => {
    try {
      await toggleAccountFavorite(id, false);
      toast({
        title: 'Sucesso',
        description: 'Favorecido removido',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchFavorites();
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao remover favorecido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este favorecido?')) return;
    try {
      await deleteAccount(id);
      toast({
        title: 'Sucesso',
        description: 'Favorecido excluído',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchFavorites();
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao excluir favorecido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row: FavoriteAccount) => {
    setEditing(row);
    setModalOpen(true);
  };

  const columns = useMemo(
    () =>
      [
        {
          Header: 'Nome',
          accessor: 'accountHolder',
          Cell: ({ value }: any) => value || '---',
        },
        {
          Header: 'Chave PIX / Dados bancários',
          accessor: 'pix',
          Cell: ({ row }: any) => {
            const { pix, agency, account } = row.original;
            return pix || `${agency || ''} - ${account || ''}`;
          },
        },
        {
          Header: 'Banco',
          accessor: 'bankIspb',
          Cell: ({ value }: any) => value || '---',
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Input
        placeholder="Buscar por nome ou chave PIX..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        mb={4}
        size="sm"
        variant="outline"
        rounded="sm"
      />
      <TableData
        title="Favorecidos"
        pageEnable={true}
        rightActions={
          <Button size="sm" colorScheme="brand" leftIcon={<FaPlus />} onClick={openCreate}>
            Cadastrar favorito
          </Button>
        }
        actions={(row: FavoriteAccount) => (
          <ButtonGroup>
            <Tooltip label="Editar favorecido">
              <IconButton
                onClick={() => openEdit(row)}
                variant="outline"
                size="sm"
                colorScheme="blue"
                aria-label="Editar favorecido"
                icon={<FaEdit />}
              />
            </Tooltip>
            <Tooltip label="Excluir favorecido">
              <IconButton
                onClick={() => handleDelete(row.id)}
                variant="outline"
                size="sm"
                colorScheme="red"
                aria-label="Excluir favorecido"
                icon={<FaTrash />}
              />
            </Tooltip>
            <Tooltip label="Remover dos favoritos">
              <IconButton
                onClick={() => handleRemoveFavorite(row.id)}
                variant="outline"
                size="sm"
                colorScheme="orange"
                aria-label="Remover dos favoritos"
                icon={<FaHeartBroken />}
              />
            </Tooltip>
          </ButtonGroup>
        )}
        columnsData={columns}
        tableData={data}
      />
      <FavoriteFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchFavorites}
        favorite={editing}
      />
    </Box>
  );
}
